import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ISortingGrade } from "../../../utils/data-types";
import { monthString } from "../../../utils/functions";
import "./WasteAmountBarChart.scss";

interface DataProps {
  dataYear1: ISortingGrade[] | undefined;
  dataYear2: ISortingGrade[] | undefined;
  year1: number;
  year2: number | undefined;
  diagramView: string;
  month: number;
  monthlyView: boolean;
}

export const WasteAmountBarChart = ({
  dataYear1,
  dataYear2,
  year1,
  year2,
  diagramView,
  month,
  monthlyView,
}: DataProps) => {
  const totalMengdeYear1: { [artikkel: string]: number } = {};
  const totalMengdeYear2: { [artikkel: string]: number } = {};

  dataYear1?.forEach((entry) => {
    totalMengdeYear1[entry.tekst] =
      (totalMengdeYear1[entry.tekst] || 0) + entry.mengde;
  });

  dataYear2?.forEach((entry) => {
    totalMengdeYear2[entry.tekst] =
      (totalMengdeYear2[entry.tekst] || 0) + entry.mengde;
  });

  const combinedData: { [tekst: string]: { [key: string]: number } } = {};

  const artikkelsSet = new Set([
    ...(dataYear1?.map((entry) => entry.tekst) as string[]),
    ...(dataYear2?.map((entry) => entry.tekst) as string[]),
  ]);

  artikkelsSet.forEach((tekst) => {
    const dataObj: { [key: string]: number } = {
      [`${year1}`]: totalMengdeYear1[tekst] || 0,
    };

    if (year2) {
      dataObj[`${year2}`] = totalMengdeYear2[tekst] || 0;
    }

    combinedData[tekst] = dataObj;
  });

  const COLORS = ["#2C5113", "#E0F1D9"];

  /*
   * Uncomment to show legend
   */
  // const legendFormatter = (value: string) => {
  //   return <span style={{ color: "#121926" }}>{value}</span>;
  // };

  const CustomTooltip = ({
    title,
    valueYear1,
    valueYear2,
    year1,
    year2,
    month,
  }: {
    title: string;
    valueYear1: string;
    valueYear2?: string;
    year1: string;
    year2?: string;
    month?: number;
  }) => {
    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "5px",
          border: "1px solid #cccccc",
        }}
      >
        <p>{title}</p>
        <p className="tooltip-value">{`${monthString(
          month ?? 0
        )} ${year1}: ${valueYear1} kg`}</p>
        {year2 && (
          <p className="tooltip-value">{`${monthString(
            month ?? 0
          )} ${year2}: ${valueYear2} kg`}</p>
        )}
      </div>
    );
  };
  function extractAndCapitalize(str: string): string {
    // Find the index of the word "av" in the string
    const avIndex = str.toLowerCase().indexOf("av");

    if (avIndex !== -1) {
      // Extract the substring after "av" and trim any leading or trailing whitespace
      let extractedStr = str.substring(avIndex + 2).trim();

      extractedStr =
        extractedStr.charAt(0).toUpperCase() + extractedStr.slice(1);

      return extractedStr;
    }

    return str;
  }

  return (
    <ResponsiveContainer width="100%" height={monthlyView ? 438 : 490}>
      <BarChart
        data={Object.entries(combinedData).map(([tekst, values]) => ({
          tekst,
          ...values,
        }))}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid vertical={false} stroke="#D0D5DD" />
        <XAxis
          dataKey="tekst"
          tickFormatter={extractAndCapitalize}
          tickMargin={25}
          axisLine={false}
          tickLine={false}
          height={50}
        />
        <YAxis orientation="left" axisLine={false} tickLine={false}>
          <Label
            value="Mengde i kg"
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        <Tooltip
          cursor={false}
          //eslint-disable-next-line
          content={({ payload }: any) => {
            if (payload && payload.length > 0) {
              const title = payload[0].payload.tekst;
              const valueYear1 = payload[0].value;
              const valueYear2 = payload[1]?.value;
              const year1 = payload[0].dataKey;
              const year2 = payload[1]?.dataKey;
              return (
                <CustomTooltip
                  title={extractAndCapitalize(title)}
                  valueYear1={valueYear1}
                  year1={year1}
                  valueYear2={valueYear2}
                  year2={year2}
                  month={monthlyView ? month : undefined}
                />
              );
            }
            return null;
          }}
        />
        {/* <Legend formatter={legendFormatter} /> */}
        <Bar
          dataKey={`${year1}`}
          maxBarSize={180}
          fill={year1 ? COLORS[0] : COLORS[1]}
          radius={[10, 10, 0, 0]}
        >
          <LabelList dataKey={`${year1}`} position="top" fill={COLORS[0]} />

          <LabelList
            formatter={() => {
              return `${year1}`;
            }}
            position="insideBottom"
            offset={-17}
            fill={COLORS[0]}
          />
        </Bar>
        {diagramView === "compare" && (
          <Bar
            dataKey={`${year2}`}
            maxBarSize={180}
            fill={COLORS[1]}
            radius={[10, 10, 0, 0]}
          >
            <LabelList dataKey={`${year2}`} position="top" fill="#749F58" />
            {dataYear2 && dataYear2[0]?.mengde > 0 && (
              <LabelList
                formatter={() => {
                  return `${year2}`;
                }}
                position="insideBottom"
                offset={-17}
                fill="#749F58"
              />
            )}
          </Bar>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};
